import { EmptyState } from "@/components/feedback/empty-state";
import { formatInr } from "@/lib/format/inr";
import { formatRelativeTime } from "@/lib/format/relative-time";
import { CategoryIcon } from "@/components/category-icon/category-icon";

export interface RecentExpense {
  id: string;
  amount: number;
  categoryName: string;
  categoryEmoji: string | null;
  note: string | null;
  createdAt: Date | string;
  creator: { name: string };
}

export interface RecentExpensesCardProps {
  data: RecentExpense[];
  now: Date;
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map(p => p[0]?.toUpperCase() ?? "")
    .join("") || "??";
}

export function RecentExpensesCard({ data, now }: RecentExpensesCardProps) {
  if (data.length === 0) {
    return <EmptyState title="No recent expenses" />;
  }

  return (
    <div className="flex flex-col">
      {data.map(exp => (
        <div
          key={exp.id}
          className="grid grid-cols-[32px_1fr_auto] items-center gap-3 border-b border-fc-border py-2.5 last:border-b-0"
        >
          {exp.categoryEmoji ? (
            <CategoryIcon emoji={exp.categoryEmoji} size="sm" />
          ) : (
            <div className="grid h-8 w-8 place-items-center rounded-lg bg-fc-overlay text-[11.5px] font-semibold text-fc-text">
              {initials(exp.creator.name)}
            </div>
          )}
          <div className="min-w-0">
            <div className="truncate text-[12.5px] text-fc-text">
              <span className="font-semibold">{exp.creator.name}</span>
              <span className="text-fc-text-dim"> · </span>
              <span>{exp.categoryName}</span>
            </div>
            {exp.note ? (
              <div className="truncate text-[11px] text-fc-text-dim">
                {exp.note}
              </div>
            ) : null}
          </div>
          <div className="flex flex-col items-end gap-1">
            <span className="font-mono text-[12.5px] font-semibold text-fc-text">
              {formatInr(exp.amount)}
            </span>
            <span className="font-mono text-[11px] text-fc-text-dim">
              {formatRelativeTime(exp.createdAt, now)}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}
